/**
 * Date formatting helpers (STRICT UI format)
 * For Media Devoted | Created By Ciro Autiero
 *
 * UI must be DD/MM/YYYY and DD/MM/YYYY HH:mm (24h).
 */

import { APP_DISPLAY_TIMEZONE, formatInZone } from '@/lib/datetime-timezone';

export function formatDateDMY(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getUTCFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Format datetime as DD/MM/YYYY HH:mm (24h) in {@link APP_DISPLAY_TIMEZONE}.
 * All users see the same wall time; store UTC in the API/DB.
 */
export function formatDateTimeDMYHM(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return formatInZone(d, APP_DISPLAY_TIMEZONE);
}

/**
 * Parse flexible date string (ISO, DD/MM/YYYY HH:mm, etc.) and return DD/MM/YYYY HH:mm.
 * Returns null if invalid.
 */
export function toSentAtFormat(s: string | null | undefined): string | null {
  if (!s?.trim()) return null;
  const trimmed = s.trim();
  const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
  const d = m
    ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4]), Number(m[5]), 0)
    : new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateTimeDMYHM(d);
}

/** Parse DD/MM/YYYY or YYYY-MM-DD to ISO (YYYY-MM-DD). Returns empty string if invalid. */
export function dmyToIso(input: string): string {
  const s = (input || '').trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mt = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${mt}-${dy}`;
}

/** Convert ISO (YYYY-MM-DD) to DD/MM/YYYY for URL/display. */
export function isoToDmy(iso: string): string {
  const [yyyy, mm, dd] = (iso || '').split('-');
  if (!yyyy || !mm || !dd) return '';
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Today's date (YYYY-MM-DD) in the reporting timezone (Europe/Paris) — the tz the
 * Voluum/Binom reports use. So the default stats range = the report's "today",
 * not the UTC day (which is off by a day just after Paris midnight).
 */
export function todayIsoDateParis(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string): string =>
    parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Add days to an ISO date (YYYY-MM-DD). Returns ISO date string. */
export function addDaysToIso(iso: string, days: number): string {
  const d = new Date(iso + 'T12:00:00.000Z');
  d.setUTCDate(d.getUTCDate() + days);
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** First day of month for given ISO date (or today). */
export function firstDayOfMonthIso(iso?: string): string {
  const base = iso ? new Date(iso + 'T12:00:00.000Z') : new Date();
  const yyyy = String(base.getUTCFullYear());
  const mm = String(base.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}-01`;
}

/** Last day of month for given ISO date (or today). */
export function lastDayOfMonthIso(iso?: string): string {
  const base = iso ? new Date(iso + 'T12:00:00.000Z') : new Date();
  const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0));
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** First day of year for given ISO date (or today). */
export function firstDayOfYearIso(iso?: string): string {
  const base = iso ? new Date(iso + 'T12:00:00.000Z') : new Date();
  const yyyy = String(base.getUTCFullYear());
  return `${yyyy}-01-01`;
}

/** Last day of year for given ISO date (or today). */
export function lastDayOfYearIso(iso?: string): string {
  const base = iso ? new Date(iso + 'T12:00:00.000Z') : new Date();
  const yyyy = String(base.getUTCFullYear());
  return `${yyyy}-12-31`;
}

/** Add months to an ISO date. */
export function addMonthsToIso(iso: string, months: number): string {
  const d = new Date(iso + 'T12:00:00.000Z');
  d.setUTCMonth(d.getUTCMonth() + months);
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Extract prefix from titleBase (e.g. "AU_CASINO 14/02/2026" → "AU_CASINO").
 * Used when duplicating to a new date.
 */
export function extractTitlePrefix(titleBase: string): string {
  const cleaned = stripTitleSequenceNumber(titleBase || '').trim();
  const match = cleaned.match(/^(.+?)\s+(\d{2}\/\d{2}\/\d{4})$/);
  return match ? match[1]!.trim() : cleaned || 'DRAFT';
}

/** Build title base like "AU_CASINO 06/02/2025" from prefix and ISO date. */
export function buildTitleBase(prefix: string, workDate: string): string {
  const [yyyy, mm, dd] = workDate.split('-');
  const dmy = `${dd}/${mm}/${yyyy}`;
  return `${prefix} ${dmy}`.trim();
}

/**
 * Strip trailing #N sequence(s) from a drop title.
 * e.g. "AU_CASINO 14/02/2026 #1 #1 #1" → "AU_CASINO 14/02/2026"
 * The backend assigns #1, #2, #3 on send; frontend must never include these in titleBase.
 */
export function stripTitleSequenceNumber(title: string): string {
  if (!title || typeof title !== 'string') return title;
  return title.replace(/(\s+#\d+)+$/g, '').trim();
}

